import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  CheckCircleIcon,
  ClockIcon,
  EyeIcon,
  UserCircleIcon,
  UserGroupIcon,
  UserIcon,
  TruckIcon,
  QuestionMarkCircleIcon,
  MapPinIcon,
  ShieldExclamationIcon,
} from "@heroicons/react/24/outline";
import { useAuthFetch } from "../../hooks/useAuthFetch";
import { AuthContext } from "../../contexts/AuthContext";
import { SideSheet } from "../../components/sheet/sideSheet";
import { TextInput } from "../../components/textInput";
import { TextArea } from "../../components/textArea";
import { Checkbox } from "../../components/checkbox";
import { Toast } from "../../components/toast";
import { UserProfile } from "../../components/profile";
import { MultiSelect } from "../../components/menu/multiSelect";
import Datepicker from "react-tailwindcss-datepicker";
import SegmentedSwitch from "../../components/segmentedSwitch";
import classNames from "../../utils/classNames";
import { MessagePhotos } from "./photos";
import {
  API,
  MESSAGE_TYPES,
  TYPE_OPTIONS,
  SUBJECT_OPTIONS,
  TIME_OPTIONS,
  toDateInput,
  formatDateTime,
} from "./messageUtils";

const AUDIENCE_OPTIONS = [
  { value: "all", text: "Everyone clocked in" },
  { value: "sites", text: "Clocked in at sites" },
];

const SUBJECT_LABELS = { person: "Person", vehicle: "Vehicle", other: "Other" };

const BOLO_PLACEHOLDERS = {
  person: "Man in a red hoodie trying car doors",
  vehicle: "White pickup circling the loading dock",
  other: "Unattended bag left in lobby B",
};

const schema = z.object({
  type: z.enum(["general", "bolo"]),
  title: z.string().trim().min(1, "Give the message a title").max(255),
  body: z.string().optional(),
  subject: z.enum(["person", "vehicle", "other"]),
  instructions: z.string().optional(),
  last_seen_location: z.string().max(255).optional(),
  last_seen_date: z.string().optional(),
  last_seen_time: z.string().optional(),
  vehicle_plate: z.string().max(20, "Plates are 20 characters or fewer").optional(),
  vehicle_make_model: z.string().max(100).optional(),
  vehicle_color: z.string().max(50).optional(),
  requires_ack: z.boolean().optional(),
});

const EMPTY = {
  type: "general",
  title: "",
  body: "",
  subject: "person",
  instructions: "",
  last_seen_location: "",
  last_seen_date: "",
  last_seen_time: "",
  vehicle_plate: "",
  vehicle_make_model: "",
  vehicle_color: "",
  requires_ack: false,
};

const Label = ({ children }) => (
  <span className="block text-sm font-medium mb-1 text-gray-600 dark:text-gray-300">
    {children}
  </span>
);

// The message sheet. With no `messageId` it composes and sends a new message.
// With one, it shows the sent message read-only (sent messages can't change;
// a correction is a new message) plus who has read and confirmed it.
// `prefill` can preselect the type or sites, or ({ tab: "Recipients" }) open a
// sent message scrolled to its recipients.
function MessageDetails({ isOpen, onClose, messageId, prefill, onSaved }) {
  const [toast, setToast] = useState({ show: false, message: "", type: "" });

  return (
    <div>
      {messageId ? (
        <SentMessage isOpen={isOpen} onClose={onClose} messageId={messageId} prefill={prefill} />
      ) : (
        <Compose
          isOpen={isOpen}
          onClose={onClose}
          prefill={prefill}
          onSaved={onSaved}
          setToast={setToast}
        />
      )}
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onHide={() => setToast((prev) => ({ ...prev, show: false }))}
      />
    </div>
  );
}

function Compose({ isOpen, onClose, prefill, onSaved, setToast }) {
  const authFetch = useAuthFetch();
  const { authContext } = useContext(AuthContext);

  const methods = useForm({
    mode: "onChange",
    resolver: zodResolver(schema),
    defaultValues: EMPTY,
  });
  const { reset, handleSubmit, watch, setValue, formState } = methods;
  const type = watch("type");
  const subject = watch("subject");
  const lastSeenDate = watch("last_seen_date");
  const lastSeenTime = watch("last_seen_time");
  const typeDef = MESSAGE_TYPES[type];
  const isBolo = type === "bolo";

  const [audience, setAudience] = useState("all");
  const [siteIds, setSiteIds] = useState([]);
  const [sites, setSites] = useState([]);
  const [pendingPhotos, setPendingPhotos] = useState([]);

  useEffect(() => {
    if (!isOpen) return;
    setPendingPhotos([]);
    setAudience(prefill?.site_ids?.length ? "sites" : "all");
    setSiteIds(prefill?.site_ids || []);
    reset({ ...EMPTY, type: prefill?.type || "general" });
  }, [isOpen, prefill, reset]);

  useEffect(() => {
    if (!isOpen) return;
    authFetch(`${API}/v1/sites?org_id=${authContext.organization.orgId}`)
      .then((res) => (res.ok ? res.json() : []))
      .then((rows) => setSites(rows.map((s) => ({ id: s.id, name: s.site_name }))))
      .catch((error) => console.error("Error fetching sites", error));
  }, [isOpen, authFetch, authContext.organization.orgId]);

  const send = async (data) => {
    if (audience === "sites" && siteIds.length === 0) {
      setToast({ show: true, message: "Choose at least one site", type: "danger" });
      return;
    }
    // Date with no time = start of that day; time with no date = today.
    const lastSeenAt =
      data.last_seen_date || data.last_seen_time
        ? new Date(
            `${data.last_seen_date || toDateInput(Date.now())}T${data.last_seen_time || "00:00"}`
          ).toISOString()
        : null;
    const details = isBolo
      ? {
          subject: data.subject,
          instructions: data.instructions,
          last_seen_location: data.last_seen_location,
          last_seen_at: lastSeenAt,
          vehicle_plate: data.vehicle_plate,
          vehicle_make_model: data.vehicle_make_model,
          vehicle_color: data.vehicle_color,
        }
      : {};

    // One request with the photos attached, so the message is complete
    // before the push goes out.
    const formData = new FormData();
    formData.append(
      "data",
      JSON.stringify({
        type: data.type,
        title: data.title,
        body: data.body,
        details,
        requires_ack: data.requires_ack,
        audience,
        site_ids: siteIds,
      })
    );
    pendingPhotos.forEach((file) => formData.append("photos", file));

    try {
      const response = await authFetch(`${API}/v1/messages`, { method: "POST", body: formData });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `Status ${response.status}`);
      }
      const saved = await response.json();
      const noOne = !saved.recipient_count;
      setToast({
        show: true,
        message: noOne
          ? "Saved, but no guards are clocked in right now"
          : `Sent to ${saved.recipient_count} guard${saved.recipient_count === 1 ? "" : "s"}`,
        type: noOne ? "alert" : "success",
      });
      onSaved();
      onClose();
    } catch (error) {
      console.error("Error sending message:", error);
      setToast({ show: true, message: error.message, type: "danger" });
    }
  };

  const toggleSite = (id) =>
    setSiteIds((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));

  return (
    <FormProvider {...methods}>
      <SideSheet
        isOpen={isOpen}
        onClose={onClose}
        title="New message"
        size="large"
        isAddMode
        footerText="Send"
        handleSave={handleSubmit(send)}
        primaryButtonDisabled={formState.isSubmitting}
      >
        <div className="flex-1 px-4 sm:px-6 py-6">
          <div className="space-y-6">
            <div>
              <Label>Type</Label>
              <SegmentedSwitch
                options={TYPE_OPTIONS}
                selected={type}
                onChange={(value) => setValue("type", value)}
              />
            </div>

            {isBolo && (
              <div>
                <Label>Looking for</Label>
                <SegmentedSwitch
                  options={SUBJECT_OPTIONS}
                  selected={subject}
                  onChange={(value) => setValue("subject", value, { shouldDirty: true })}
                />
              </div>
            )}

            <TextInput
              name="title"
              label={isBolo ? "Headline" : "Title"}
              placeholder={isBolo ? BOLO_PLACEHOLDERS[subject] : "Parking lot B closed Friday"}
            />

            {isBolo && subject === "vehicle" && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <TextInput name="vehicle_plate" label="Plate" placeholder="ABC123" />
                <TextInput name="vehicle_color" label="Color" placeholder="White" />
                <TextInput name="vehicle_make_model" label="Make / model" placeholder="Ford F-150" />
              </div>
            )}

            <TextArea
              name="body"
              label={isBolo ? "Description" : "Message"}
              rows={isBolo ? 4 : 6}
              placeholder={
                !isBolo
                  ? "What do guards need to know?"
                  : subject === "vehicle"
                  ? "Dents, stickers, roof rack, number of occupants"
                  : "Height, build, hair, clothing, anything distinctive"
              }
            />

            <MessagePhotos pendingFiles={pendingPhotos} onPendingChange={setPendingPhotos} />

            {isBolo && (
              <>
                <TextInput
                  name="last_seen_location"
                  label="Last seen"
                  placeholder="North parking lot"
                />
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Date</Label>
                    <Datepicker
                      asSingle={true}
                      useRange={false}
                      showTwoCalendars={false}
                      value={{ startDate: lastSeenDate || null, endDate: lastSeenDate || null }}
                      onChange={(value) => {
                        // The picker gives "YYYY-MM-DD" or a Date; keep the
                        // string as is (parsing it would shift it to UTC).
                        const picked = value?.startDate;
                        setValue(
                          "last_seen_date",
                          !picked
                            ? ""
                            : typeof picked === "string"
                            ? picked.slice(0, 10)
                            : toDateInput(picked)
                        );
                      }}
                      maxDate={new Date()}
                      placeholder="YYYY-MM-DD"
                      inputName="last_seen_date"
                      inputClassName="h-9 w-full rounded-md px-3 items-center text-sm leading-none tracking-normal font-normal border-0 pr-10 focus:outline-none text-gray-600 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/[0.06] bg-gray-50 dark:bg-gray-800"
                    />
                  </div>
                  <div>
                    <Label>Time</Label>
                    <MultiSelect
                      label="Time"
                      labelPosition="none"
                      options={TIME_OPTIONS}
                      selected={lastSeenTime ? [lastSeenTime] : []}
                      onUpdate={([value]) => setValue("last_seen_time", value)}
                      singleSelect
                      fullWidth
                      matchTriggerWidth
                    />
                  </div>
                </div>
                <TextArea
                  name="instructions"
                  label="What guards should do"
                  rows={3}
                  placeholder="Do not approach. Call dispatch and note the direction of travel."
                />
              </>
            )}

            <div>
              <Label>Send to</Label>
              <SegmentedSwitch options={AUDIENCE_OPTIONS} selected={audience} onChange={setAudience} />
              {audience === "sites" && (
                <div className="mt-3 max-h-48 overflow-y-auto rounded-md bg-gray-50 dark:bg-gray-800 p-2">
                  {sites.length === 0 ? (
                    <p className="p-2 text-sm text-gray-400">No sites yet</p>
                  ) : (
                    sites.map((site) => (
                      <label
                        key={site.id}
                        className="flex cursor-pointer items-center gap-3 rounded px-2 py-1.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/[0.06]"
                      >
                        <input
                          type="checkbox"
                          checked={siteIds.includes(site.id)}
                          onChange={() => toggleSite(site.id)}
                          className="focus:ring-brand-primary h-4 w-4 text-indigo-600 border-gray-300 rounded"
                        />
                        {site.name}
                      </label>
                    ))
                  )}
                </div>
              )}
            </div>

            {typeDef.requiresAck ? (
              <p className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                <CheckCircleIcon className="h-5 w-5 text-gray-400" />
                Guards must tap "Confirm message".
              </p>
            ) : (
              <Checkbox name="requires_ack" label="Ask guards to confirm" />
            )}
          </div>
        </div>
      </SideSheet>
    </FormProvider>
  );
}

// A sent message, read-only: what was sent, then who has read and confirmed it.
function SentMessage({ isOpen, onClose, messageId, prefill }) {
  const authFetch = useAuthFetch();
  const { authContext } = useContext(AuthContext);
  const [message, setMessage] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [siteNames, setSiteNames] = useState("");
  const recipientsRef = useRef(null);

  const load = useCallback(async () => {
    const [messageRes, recipientsRes] = await Promise.all([
      authFetch(`${API}/v1/messages/${messageId}`),
      authFetch(`${API}/v1/messages/${messageId}/recipients`),
    ]);
    const full = messageRes.ok ? await messageRes.json() : null;
    setMessage(full);
    setRecipients(recipientsRes.ok ? await recipientsRes.json() : []);
    if (full?.audience === "sites") {
      const sitesRes = await authFetch(
        `${API}/v1/sites?org_id=${authContext.organization.orgId}`
      );
      const rows = sitesRes.ok ? await sitesRes.json() : [];
      setSiteNames(
        rows
          .filter((s) => full.site_ids.includes(s.id))
          .map((s) => s.site_name)
          .join(", ")
      );
    }
  }, [messageId, authFetch, authContext.organization.orgId]);

  useEffect(() => {
    if (!isOpen) return;
    setMessage(null);
    setRecipients([]);
    setSiteNames("");
    load().catch((error) => console.error("Error fetching message", error));
  }, [isOpen, load]);

  // Opened from a "Read" / "Confirmed" count or the Dashboard: jump to who.
  useEffect(() => {
    if (message && prefill?.tab === "Recipients") {
      recipientsRef.current?.scrollIntoView({ block: "start" });
    }
  }, [message, prefill]);

  const label = message ? MESSAGE_TYPES[message.type]?.label || "Message" : "Message";

  return (
    <SideSheet
      isOpen={isOpen}
      onClose={onClose}
      title={message ? `${label}: ${message.title}` : "Message"}
      size="large"
      displayFooter={false}
    >
      {message && (
        <>
          <SentDetails message={message} siteNames={siteNames} />
          <div ref={recipientsRef}>
            {/* Inset divider, lined up with the sheet's content. */}
            <div className="mx-4 sm:mx-6 border-t border-gray-100 dark:border-gray-800" />
            <Recipients message={message} recipients={recipients} />
          </div>
        </>
      )}
    </SideSheet>
  );
}

// One fact about a sent message: an icon (named for screen readers) and text.
const Fact = ({ icon: Icon, label, children }) =>
  children ? (
    <div className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-200">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
      <span className="sr-only">{label}: </span>
      <span>{children}</span>
    </div>
  ) : null;

const SUBJECT_ICONS = { person: UserIcon, vehicle: TruckIcon, other: QuestionMarkCircleIcon };

// What was sent. Facts first in two columns, then the message itself.
function SentDetails({ message, siteNames }) {
  const isBolo = message.type === "bolo";
  const d = message.details || {};
  const vehicle =
    isBolo && d.subject === "vehicle"
      ? [
          d.vehicle_plate && `Plate ${d.vehicle_plate}`,
          [d.vehicle_color, d.vehicle_make_model].filter(Boolean).join(" "),
        ]
          .filter(Boolean)
          .join(", ")
      : null;
  const lastSeen = isBolo
    ? [d.last_seen_location, formatDateTime(d.last_seen_at)].filter(Boolean).join(", ")
    : null;

  return (
    <div className="flex-1 px-4 sm:px-6 py-6 space-y-6">
      <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
        <Fact icon={ClockIcon} label="Sent">
          {formatDateTime(message.created_at)}
        </Fact>
        <Fact icon={UserCircleIcon} label="Sent by">
          {message.created_by_name}
        </Fact>
        <Fact icon={UserGroupIcon} label="Sent to">
          {message.audience === "sites"
            ? `Clocked in at ${siteNames || "selected sites"}`
            : "Everyone clocked in"}
        </Fact>
        <Fact icon={CheckCircleIcon} label="Confirmation">
          {message.requires_ack ? "Confirmation required" : "No confirmation needed"}
        </Fact>
        {isBolo && (
          <Fact icon={SUBJECT_ICONS[d.subject] || UserIcon} label="Looking for">
            {vehicle || SUBJECT_LABELS[d.subject]}
          </Fact>
        )}
        <Fact icon={MapPinIcon} label="Last seen">
          {lastSeen && `Last seen ${lastSeen}`}
        </Fact>
      </div>

      {message.body && (
        <div>
          {/* Same label as the compose form uses for this field. */}
          <Label>{isBolo ? "Description" : "Message"}</Label>
          <p className="text-sm text-gray-800 dark:text-gray-100 whitespace-pre-line">
            {message.body}
          </p>
        </div>
      )}

      <MessagePhotos photos={message.photos || []} />

      {isBolo && d.instructions && (
        <div className="flex items-start gap-2 rounded-md bg-gray-50 dark:bg-gray-800 p-3 text-sm text-gray-800 dark:text-gray-100">
          <ShieldExclamationIcon className="mt-0.5 h-4 w-4 shrink-0 text-danger" aria-hidden="true" />
          <span className="sr-only">What guards should do: </span>
          <span className="whitespace-pre-line">{d.instructions}</span>
        </div>
      )}
    </div>
  );
}

// Who the message went to. Outstanding first so it's clear who to chase.
function Recipients({ message, recipients }) {
  const needsConfirm = message.requires_ack;
  if (recipients.length === 0) {
    return (
      <div className="px-4 sm:px-6 py-6">
        <Label>Recipients</Label>
        <p className="text-sm text-gray-400 dark:text-gray-500">
          No guards were clocked in when this was sent.
        </p>
      </div>
    );
  }
  return (
    <div className="px-4 sm:px-6 py-6">
      <Label>Recipients</Label>
      <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
        Read by {message.read_count} of {message.recipient_count}
        {needsConfirm ? `, confirmed by ${message.acknowledged_count}` : ""}
      </p>
      <ul className="divide-y divide-gray-100 dark:divide-gray-800">
        {recipients.map((r) => (
          <li key={r.user_id} className="flex items-center justify-between py-3">
            {/* UserProfile brings its own 12px right margin, like the users table. */}
            <div className="flex items-center">
              <UserProfile
                imageUrl={r.profile_url}
                size="small"
                firstName={r.first_name}
                lastName={r.last_name}
                useInitials={!r.profile_url}
              />
              <span className="text-sm text-gray-800 dark:text-gray-100">
                {r.first_name} {r.last_name}
              </span>
            </div>
            <RecipientStatus recipient={r} needsConfirm={needsConfirm} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function RecipientStatus({ recipient, needsConfirm }) {
  const [Icon, text, tone] = recipient.acknowledged_at
    ? [CheckCircleIcon, `Confirmed ${formatDateTime(recipient.acknowledged_at)}`, "text-success"]
    : recipient.read_at
    ? [
        EyeIcon,
        `Read ${formatDateTime(recipient.read_at)}`,
        needsConfirm ? "text-gray-500 dark:text-gray-400" : "text-success",
      ]
    : [ClockIcon, "Not opened", "text-gray-400 dark:text-gray-500"];
  return (
    <span className={classNames("flex items-center gap-1.5 text-xs", tone)}>
      <Icon className="h-4 w-4" />
      {text}
    </span>
  );
}

export { MessageDetails, RecipientStatus };
